<template>
    <main class="p-2 w-full h-full max-h-main flex flex-col items-start min-h-main">
        <div class="w-full">
            <div class="flex w-full gap-2 justify-between">
                <app-button @click="addAmendment()" :label="$t('amendments.newAmendment')" v-if="!readOnly" />
                <div class="border px-1">
                    <table>
                        <tr>
                            <th class="p-1">{{ $t('amendments.cieBalance') }}:</th>
                            <td class="p-1">
                                {{
                                    cieBalance > 0
                                        ? '+' + $n(cieBalance, currencyFormat)
                                        : cieBalance < 0
                                        ? $n(cieBalance, currencyFormat)
                                        : '0.00'
                                }}
                            </td>
                        </tr>
                    </table>
                </div>
            </div>
            <app-multi-picker
                ref="filter"
                icon="icon-magnify"
                :allowStringCriteria="true"
                class="w-full my-2"
                v-model="filterValue"
                :options="filterOptions"
                :strictMatching="true"
            >
                <template v-slot:option="{ option }">
                    <span>{{ option.fullName || option.name }}</span>
                    <span class="text-xs text-gray-600 ml-1">{{ option.criteriaType }}</span>
                </template>
            </app-multi-picker>
        </div>
        <div class="flex w-full flex-grow overflow-auto flex-col text-xs relative">
            <div v-if="loading" class="flex justify-center">
                <icon-rotate-right class="animate animate-spin"></icon-rotate-right>
            </div>
            <table class="table-fixed" v-else>
                <thead class="sticky top-0 bg-white">
                    <tr>
                        <th class="text-left border-r p-1" style="width: 15rem">
                            <button
                                class="hover:underline font-bold w-full flex justify-between"
                                @click="sortBy('emitter')"
                            >
                                {{ $t('commons.bundle') }}
                                <div v-if="sortKey === 'emitter'">
                                    <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                    <icon-menu-down v-else width="16" height="16" />
                                </div>
                            </button>
                        </th>
                        <th class="text-left border-r p-1">
                            <button
                                class="hover:underline font-bold w-full flex justify-between"
                                @click="sortBy('object')"
                            >
                                {{ $t('amendments.object') }}
                                <div v-if="sortKey === 'object'">
                                    <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                    <icon-menu-down v-else width="16" height="16" />
                                </div>
                            </button>
                        </th>
                        <th class="text-center border-r p-1" style="width: 6rem">
                            <button
                                class="hover:underline font-bold w-full flex justify-between"
                                @click="sortBy('amountHT')"
                            >
                                <div class="flex flex-col">
                                    <span>{{ $t('amendments.amountHT') }}</span>
                                    ({{
                                        filteredTotal > 0
                                            ? '+' + $n(filteredTotal, currencyFormat)
                                            : filteredTotal < 0
                                            ? $n(filteredTotal, currencyFormat)
                                            : '0.00'
                                    }})
                                </div>
                            </button>
                        </th>
                        <th style="width: 3rem" class="hidden sm:table-cell text-center border-r p-1">
                            <button
                                class="hover:underline font-bold w-full flex justify-between"
                                @click="sortBy('quoteStatus')"
                            >
                                {{ $t('commons.status') }}
                            </button>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="amendment in filteredItems">
                        <tr class="odd:bg-blue-50 border-t">
                            <td class="text-left border-r p-1">
                                <div
                                    :title="
                                        amendment.bundle && amendment.bundle.company
                                            ? amendment.bundle.company.name
                                            : ''
                                    "
                                >
                                    {{ amendment.bundle ? amendment.bundle.label : '' }}
                                </div>
                            </td>
                            <td class="border-r p-1">
                                <template v-if="amendment.amendmentObject">
                                    <div class="flex gap-1">
                                        <span class="font-bold">{{ amendment.object }}</span>
                                        <span class="">- {{ amendment.bundleTo.label }} -</span>
                                        <router-link
                                            class="hover:underline flex gap-1"
                                            :to="{
                                                name: 'cieAmendment',
                                                params: {
                                                    ...$route.params,
                                                    amendmentId: amendment.id,
                                                },
                                            }"
                                        >
                                            <span>{{ amendment.amendmentObject }}</span>
                                            <span v-if="amendment.code" class="mx-1">#{{ amendment.code }}</span>
                                            <span v-if="!amendment.object || amendment.object.trim().length === 0">
                                                ...
                                            </span>
                                        </router-link>
                                    </div>
                                </template>
                                <template v-else>
                                    <router-link
                                        class="hover:underline flex gap-1"
                                        :to="{
                                            name: 'cieAmendment',
                                            params: {
                                                ...$route.params,
                                                amendmentId: amendment.id,
                                            },
                                        }"
                                    >
                                        <span class="font-bold">{{ amendment.object }}</span>
                                        <span v-if="amendment.code" class="mx-1">#{{ amendment.code }}</span>
                                        <span v-if="!amendment.object || amendment.object.trim().length === 0">
                                            ...
                                        </span>
                                    </router-link>
                                </template>
                            </td>
                            <td
                                class="border-r p-1 text-right"
                                :class="{
                                    'text-green-500': amendment.amountHT >= 0,
                                    'text-red-600': amendment.amountHT < 0,
                                }"
                            >
                                {{
                                    amendment.amountHT
                                        ? (amendment.amountHT > 0 ? '+' : '') + $n(amendment.amountHT, currencyFormat)
                                        : '-'
                                }}
                            </td>
                            <td class="text-left border-r p-1" :class="amendment.quoteStatusColor">
                                {{ $t('amendments.quoteStatus.' + amendment.quoteStatus) }}
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
        <app-quick-actions :options="quickActions" @choose="$event.run()"></app-quick-actions>
    </main>
</template>

<script>
import { isMobile } from '@/state/state';
import { filterMatch, get, getMapById, sortBy, uniqBy } from '@/services/sanitize.service';
import AppMultiPicker from '@/components/appMultiPicker/AppMultiPicker';
import AppQuickActions from '@/components/appQuickActions/AppQuickActions';
import AppButton from '@/components/appButton/AppButton';
import { createAmendment, getAmendments } from '@/features/amendments/amendments.service';
import IconMenuDown from '@/icons/IconMenuDown';
import IconMenuUp from '@/icons/IconMenuUp';
import { queryProject } from '@/features/projects/projects.service';
import { combineLatest } from 'rxjs';
import { getBundles } from '@/features/bundles/bundles.service';
import { getCompanies } from '@/features/companies/companies.service';

export default {
    components: {
        IconMenuUp,
        IconMenuDown,
        AppButton,
        AppQuickActions,
        AppMultiPicker,
    },
    async created() {
        this.restoreFilter();
        queryProject(this.$route.params.projectId).then((project) => {
            this.readOnly = !project.me.allowedFeatures.includes('project_amendments');
            this.currencyFormat.currency = project.currency;
        });
        this.init();
    },
    computed: {
        filteredTotal() {
            return (
                Math.round(
                    this.filteredItems.reduce((acc, item) => (item.amountHT ? item.amountHT + acc : acc), 0) * 100,
                ) / 100
            );
        },
        cieBalance() {
            return (
                Math.round(
                    this.items
                        .filter((item) => item.quoteStatus === 'validated')
                        .reduce((acc, item) => (item.amountHT ? item.amountHT + acc : acc), 0) * 100,
                ) / 100
            );
        },
        filteredItems() {
            let result = this.filterFn(this.filterValue);
            if (this.sortKey) {
                result = sortBy(result, (item) => {
                    if (this.sortKey === 'object') {
                        return item.object;
                    } else if (this.sortKey === 'amountHT') {
                        return item.amountHT;
                    } else if (this.sortKey === 'quoteStatus') {
                        return item.quoteStatus;
                    } else if (this.sortKey === 'emitter') {
                        return item.bundle ? item.bundle.label : null;
                    }
                });
                if (!this.sortAsc) {
                    result.reverse();
                }
            }
            return result;
        },
        filterOptions() {
            return [
                {
                    isGroup: true,
                    name: this.$t('commons.status'),
                    id: this.$t('commons.status'),
                    children: [
                        {
                            id: 'quote_coming',
                            name: this.$t('amendments.comingQuotes'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                        {
                            id: 'quote_toEmit',
                            name: this.$t('amendments.quoteToEmit'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                        {
                            id: 'quote_toAnalyse',
                            name: this.$t('amendments.quoteToAnalyse'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                        {
                            id: 'quote_toNegotiate',
                            name: this.$t('amendments.quoteToNegotiate'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                        {
                            id: 'quote_toValidateMOA',
                            name: this.$t('amendments.quoteToValidateMOA'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                        {
                            id: 'quote_validated',
                            name: this.$t('amendments.quoteValidated'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                        {
                            id: 'quote_rejected',
                            name: this.$t('amendments.quoteRejected'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                        {
                            id: 'quote_obsolete',
                            name: this.$t('amendments.quoteObsolete'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                        {
                            id: 'quote_isMarket',
                            name: this.$t('amendments.quoteIsMarket'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                        {
                            id: 'quote_isMarketOption',
                            name: this.$t('amendments.quoteIsMarketOption'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                        {
                            id: 'quote_noImpact',
                            name: this.$t('amendments.quoteNoImpact'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                    ],
                },
                {
                    isGroup: true,
                    name: this.$t('commons.type'),
                    id: this.$t('commons.type'),
                    children: [
                        {
                            id: 'cie',
                            name: this.$t('amendments.types.cie'),
                            firstOfCriteriaType: false,
                            _isTypeCriteria: true,
                        },
                        {
                            id: 'engagedCIE',
                            name: this.$t('amendments.engagedCIEShort'),
                            firstOfCriteriaType: false,
                            _isTypeCriteria: true,
                        },
                    ],
                },
                {
                    isGroup: true,
                    name: this.$t('commons.emitterCriteriaType'),
                    id: this.$t('commons.emitterCriteriaType'),
                    children: this.bundles.map((bundle, index) => ({
                        ...bundle,
                        name: bundle.label,
                        firstOfCriteriaType: index === 0,
                        criteriaType: this.$t('commons.emitterCriteriaType'),
                        _isBundleCriteria: true,
                    })),
                },
            ];
        },
    },
    methods: {
        init() {
            this.subscriptions = [
                combineLatest([
                    getBundles(this.$route.params.projectId),
                    getAmendments(this.$route.params.projectId, this.phase),
                    getCompanies(this.$route.params.projectId),
                ]).subscribe(([bundles, amendments, companies]) => {
                    this.companies = companies.map((company) => ({
                        ...company,
                        bundleIds: bundles
                            .filter((bundle) => bundle.companyId === company.id)
                            .map((bundle) => bundle.id),
                    }));
                    const companyMap = getMapById(companies);
                    this.bundles = sortBy(
                        bundles.map((bundle) => ({ ...bundle, company: companyMap[bundle.companyId] })),
                        (bundle) => bundle.label,
                    );
                    this.bundleMap = getMapById(this.bundles);
                    this.items = uniqBy(amendments, 'groupId')
                        .filter((amendment) => amendment.type === 'cie')
                        .map((amendment) => {
                            const bundle = this.bundleMap[amendment.bundleId];
                            return {
                                ...amendment,
                                bundle,
                                filterString: [amendment.code, amendment.object].join(),
                                quoteStatusColor: this.quoteStatusColors[amendment.quoteStatus] || '',
                            };
                        })
                        .reduce(
                            (acc, amendment) => [
                                ...acc,
                                amendment,
                                ...amendment.distribution.map((distribution) => ({
                                    id: amendment.id,
                                    bundleId: distribution.bundleId,
                                    bundle: this.bundleMap[distribution.bundleId],
                                    bundleTo: this.bundleMap[amendment.bundleId],
                                    amountHT: -distribution.amount,
                                    quoteStatus: amendment.quoteStatus,
                                    filterString: [amendment.code, amendment.object].join(),
                                    object: this.$t('amendments.engagedCIEShort'),
                                    amendmentObject: amendment.object,
                                    quoteStatusColor: this.quoteStatusColors[amendment.quoteStatus] || '',
                                    code: amendment.code,
                                })),
                            ],
                            [],
                        );
                    this.scrollToLastVisited();
                    this.loading = false;
                }),
            ];
        },
        scrollToLastVisited() {
            const lastVisitedAmendmentId = localStorage.getItem(
                'amendment.lastVisitedAmendmentId.' + this.$route.params.projectId,
            );
            if (lastVisitedAmendmentId) {
                setTimeout(() => {
                    const element = this.$el.querySelector('#uuid_' + lastVisitedAmendmentId);
                    if (element) element.scrollIntoView();
                }, 800);
            }
        },
        get,
        sortBy(key) {
            if (key === this.sortKey) {
                this.sortAsc = !this.sortAsc;
            } else {
                this.sortKey = key;
                this.sortAsc = true;
            }
            this.saveFilter(this.filterValue);
        },
        async addAmendment() {
            const result = await createAmendment(this.$route.params.projectId, {
                type: 'cie',
                object: '',
                visas: [],
                distribution: [],
            });
            await this.$router.push({
                name: 'cieAmendment',
                params: {
                    projectId: this.$route.params.projectId,
                    amendmentId: result.id,
                },
            });
        },
        matchBundleFilter(item, bundleIds) {
            return item.bundle && bundleIds.includes(item.bundle.id);
        },
        matchString(stringCriteria, item) {
            if (!stringCriteria || stringCriteria.length === 0) {
                return true;
            }
            return stringCriteria.find((criteria) => filterMatch(item.filterString || item.name, criteria, true));
        },
        matchState(stateCriteria, item) {
            if (!stateCriteria || stateCriteria.length === 0) {
                return true;
            }
            return stateCriteria.find(
                (criteria) =>
                    (criteria.id === 'quote_coming' && this.comingStatus.includes(item.quoteStatus)) ||
                    (item.quoteStatus && criteria.id === 'quote_' + item.quoteStatus),
            );
        },
        matchType(criteria, item) {
            if (!criteria || criteria.length === 0) {
                return true;
            }
            return criteria.find(
                (criteria) => (criteria.id === 'cie' && item.type === 'cie') || criteria.id === 'engagedCIE',
            );
        },
        saveFilter(filterValue) {
            localStorage.setItem(
                'amendments_cie_filter_' + this.$route.params.projectId,
                JSON.stringify({
                    filterValue,
                    sortKey: this.sortKey,
                }),
            );
        },
        restoreFilter() {
            const cache = localStorage.getItem('amendments_cie_filter_' + this.$route.params.projectId);
            if (cache) {
                const parsedCache = JSON.parse(cache);
                this.filterValue = parsedCache.filterValue || [];
                this.sortKey = parsedCache.sortKey || 'object';
            }
        },
        filterFn(filter) {
            this.saveFilter(filter);
            const bundleIds = [...filter.filter((aCriteria) => aCriteria._isBundleCriteria).map((bundle) => bundle.id)];
            const stringCriteria = filter
                .filter((aCriteria) => aCriteria._isStringCriteria)
                .map((aCriteria) => aCriteria.content);
            const stateCriteria = filter.filter((aCriteria) => aCriteria._isStateCriteria);
            const typeCriteria = filter.filter((aCriteria) => aCriteria._isTypeCriteria);

            return this.items.filter((item) => {
                const fullCriteria = {
                    matchBundleFilter: bundleIds.length === 0 || this.matchBundleFilter(item, bundleIds),
                    matchString: this.matchString(stringCriteria, item),
                    matchState: this.matchState(stateCriteria, item),
                    typeCriteria: this.matchType(typeCriteria, item),
                };
                return Object.values(fullCriteria).every((value) => !!value);
            });
        },
    },
    data() {
        return {
            quoteStatusColors: {
                toEmit: 'bg-amber-500',
                toAnalyse: 'text-white bg-gray-500',
                toNegotiate: '',
                toValidateMOA: 'bg-cyan-500',
                validated: 'bg-green-600',
                rejected: 'text-white bg-red-600',
                obsolete: 'text-white bg-red-600',
                market: '',
                marketOption: '',
                noImpact: '',
            },
            comingStatus: ['toEmit', 'toAnalyse', 'toNegotiate', 'toValidateMOA'],
            currencyFormat: {
                currency: 'EUR',
                currencyDisplay: 'symbol',
                style: 'currency',
                minimumFractionDigits: 2,
            },
            bundleMap: {},
            loading: 0,
            quickActions: [
                {
                    name: this.$t('amendments.newAmendment'),
                    run: () => this.addAmendment(),
                },
            ],
            sortKey: 'name',
            sortAsc: true,
            readOnly: true,
            subscriptions: [],
            bundles: [],
            companies: [],
            isMobile,
            items: [],
            filterValue: [],
        };
    },
};
</script>
